/**
 * A function that attempts to execute an asynchronous function multiple times
 * with a specified delay between retries.
 *
 * It is useful for retrying operations that might fail due to transient issues,
 * such as network requests or API calls.
 */
export async function withRetry<ReturnType>(
  promiseFn: () => Promise<ReturnType>,
  options?: WithRetryOptions,
) {
  const { maxRetryAttempts = 1, delay = 500 } = options ?? {};

  try {
    // Try to resolve a promise...
    return await promiseFn();
  } catch (error) {
    // ...and then retry (based on the max attempts number) if it didn't resolve.

    // If we reached to the last attempt without resolving, reject the promise.
    if (maxRetryAttempts <= 0) {
      // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
      return Promise.reject(error);
    }

    // Wait for a delay
    await new Promise((resolve) => {
      setTimeout(resolve, delay);
    });

    // Retry with one less attempt
    return withRetry(promiseFn, {
      delay,
      maxRetryAttempts: maxRetryAttempts - 1,
    });
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type WithRetryOptions = {
  maxRetryAttempts?: number;
  delay?: number;
};
